import { Box, useMediaQuery, useTheme, Snackbar, Alert } from "@mui/material";
import { operationsDashboardStyles } from "./styles";
import FilterComponent from "./FilterComponent";
import {
  FilterComponentRef,
  OperationsAppliedFilter,
  SimpleFilterState,
  OperationalDashboardResponse,
  OperationalDashboardL2Response,
  OperationsDashboardProps,
  RevenueTrendItem,
  ProjectMarginTrendItem,
  DirectGrossMarginTrendItem,
  ARCDeployedTrendItem,
  ARCNonDeployedTrendItem,
  AverageBillRateTrendItem,
  BenchCostTrendItem,
  ClientAllocatedCostTrendItem,
  InternalPlusInvestmentTrendItem,
  CustomerMarginTrendItem,
  EbitdaMarginTrendItem,
  SmCostTrendItem,
  OpsL2Data,
  OperationsDashboardVariant,
  GaGrossMarginTrendItem,
  PipelineAgingAnalysisItem,
  PipelineByOpportunityTypeItem,
  PipelineByProjectTypeItem,
  PipelineTimeSeriesItem,
  UtilizationTrendItem,
  WinRatioTrendItem,
} from "./type";
import ActiveFilterSection from "../ActiveFiterSection";
import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import { Content } from "./components/Content";
import { FilterModal } from "./components/FilterModal";
import { KeyboardTab } from "@mui/icons-material";
import { useOperationsDashboardData } from "./useOperationsDashboardData";
import useFetch from "../../hooks/useFetch";
import { baseUrl } from "../../config";
import dayjs from "dayjs";
import { createOperationsDashboardBuilder } from "./OperationsDashboardBuilder";
import Loading from "../Loading";
import { getDefaultDateRange } from "./utils";
import { getDisplayLabelForSelectedUnit } from "./components/UnitHierarchyFilter/serviceLineTreeUtils";
import { pipelineOpportunityTypeColors } from "./components/L2Widgets/styles/pipelineHealthStyles";
import { pipelineAgingTrendBucketLabels } from "../../utils/consts";
import {
  REVENUE_MARGIN_DASHBOARD_ID,
  REVENUE_COST_ANALYSIS_DASHBOARD_ID,
  UTILIZATION_DASHBOARD_ID,
  SALES_PIPELINE_HEALTH_DASHBOARD_ID,
  WIN_LOSS_ANALYSIS_DASHBOARD_ID,
} from "../../utils/consts";
import { DoubleDonutCategoryItem } from "./components/ChartTypes/GenericDoubleDonutChart";
const EMPTY_VALUE = "___EMPTY___";

const isOperationsL2Variant = (variant: OperationsDashboardVariant) =>
  variant !== "overview";

const isSalesPipelineHealthVariant = (variant: OperationsDashboardVariant) =>
  variant === "sales_pipeline_health";

const isWinLossAnalysisVariant = (variant: OperationsDashboardVariant) =>
  variant === "win_loss_analysis";

export const OperationsDashboard = ({
  variant = "overview",
}: OperationsDashboardProps) => {
  const getVariantDefaultDateRange = React.useCallback(() => {
    if (isOperationsL2Variant(variant)) {
      const endDate = dayjs().endOf("month");
      const startDate = dayjs().subtract(5, "month").startOf("month");
      return {
        startDate,
        endDate,
        label: `${startDate.format("MMM YYYY")} - ${endDate.format("MMM YYYY")}`,
      };
    }
    return getDefaultDateRange();
  }, [variant]);

  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { opsFilters?: SimpleFilterState; opsCostType?: string } | null;
  const incomingCostType = locationState?.opsCostType ?? null;
  const incomingFilters: SimpleFilterState | null = locationState?.opsFilters
    ? {
        ...locationState.opsFilters,
        dateStart: locationState.opsFilters.dateStart
          ? dayjs(locationState.opsFilters.dateStart)
          : null,
        dateEnd: locationState.opsFilters.dateEnd
          ? dayjs(locationState.opsFilters.dateEnd)
          : null,
      }
    : null;
  const filterRef = React.useRef<FilterComponentRef>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    businessUnits,
    businessUnitFilterTree,
    serviceLineOptions,
    serviceLineFilterTree,
    fetchServiceLineOptions,
    loading,
    fetchBusinessUnits,
    verticalOptions,
    fetchVerticalOptions,
    clearVerticalOptions,
    customerGroupOptions,
    fetchCustomerGroupOptions,
    clearCustomerGroupOptions,
    customerOptions,
    fetchCustomerOptions,
    clearCustomerOptions,
    prefillValues,
  } = useOperationsDashboardData();

  const getDefaultFilters = (): SimpleFilterState => {
    const { startDate, endDate, label } = getVariantDefaultDateRange();

    const result = {
      accounts:
        prefillValues.customerGroup?.value !== undefined &&
        customerGroupOptions.length > 0
          ? [prefillValues.customerGroup.value]
          : [],
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
      verticals:
        prefillValues.vertical?.value && prefillValues.vertical.value !== ""
          ? prefillValues.vertical.value
          : "",
      businessUnit:
        prefillValues.businessUnit?.value &&
        prefillValues.businessUnit.value !== ""
          ? prefillValues.businessUnit.value
          : "",
      serviceLine:
        prefillValues.serviceLine?.value &&
        prefillValues.serviceLine.value !== ""
          ? prefillValues.serviceLine.value
          : "",
      dateStart: startDate,
      dateEnd: endDate,
      dateLabel: label,
    };
    return result;
  };

  const getEmptyFilters = (): SimpleFilterState => {
    const { startDate, endDate, label } = getVariantDefaultDateRange();

    return {
      accounts: [],
      customers: [],
      verticals: "",
      businessUnit: "",
      serviceLine: "",
      dateStart: startDate,
      dateEnd: endDate,
      dateLabel: label,
    };
  };

  const [currentFilters, setCurrentFilters] =
    React.useState<SimpleFilterState>(() =>
      incomingFilters ?? getEmptyFilters(),
    );

  const setCurrentFiltersWithDebug = React.useCallback((newFilters: any) => {
    if (typeof newFilters === "function") {
      setCurrentFilters((prev: SimpleFilterState) => {
        const result = newFilters(prev);
        return result;
      });
    } else {
      setCurrentFilters(newFilters);
    }
  }, []);

  const [appliedFilters, setAppliedFilters] =
    React.useState<SimpleFilterState>(() =>
      incomingFilters ?? getEmptyFilters(),
    );

  const [shouldApplyfilters, setShouldApplyfilters] = React.useState(false);
  const prefillApplied = React.useRef(incomingFilters !== null);
  const userManualChanges = React.useRef<Set<string>>(new Set());
  const primaryOptionsLoaded = React.useRef(false);

  const [openErrorSnackbar, setOpenErrorSnackbar] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const isPrimaryLoadingComplete = () => {
    return (
      !loading.businessUnits &&
      !loading.serviceLineOptions &&
      !loading.verticals
    );
  };

  const hasPrefillValues = () => {
    return (
      Object.keys(prefillValues).length > 0 &&
      Object.values(prefillValues).some((value) => value?.value !== undefined)
    );
  };

  const [isFilterModalOpen, setIsFilterModalOpen] = React.useState(false);
  const [isFilterExpanded, setIsFilterExpanded] = React.useState(true);
  const [costType, setCostType] = React.useState(incomingCostType ?? "actual");

  const queryParams = useMemo(() => {
    const startDate = appliedFilters.dateStart
      ? dayjs(appliedFilters.dateStart)
      : dayjs().startOf("year");
    const endDate = appliedFilters.dateEnd
      ? dayjs(appliedFilters.dateEnd)
      : dayjs().endOf("year");

    return {
      start_year: startDate.year(),
      start_month: startDate.month() + 1,
      end_year: endDate.year(),
      end_month: endDate.month() + 1,
      business_unit: appliedFilters.businessUnit ?? "",
      service_line: appliedFilters.serviceLine ?? "",
      vertical: appliedFilters.verticals ?? "",
      customer: appliedFilters.customers
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      customer_group: appliedFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      cost_type: costType,
    };
  }, [appliedFilters, costType]);

  const overviewRequest = useMemo(() => {
    if (variant !== "overview") return null;
    return {
      method: "get" as const,
      url: `${baseUrl}/operational-dashboard`,
      params: queryParams,
    };
  }, [variant, queryParams]);

  const l2Request = useMemo(() => {
    if (!isOperationsL2Variant(variant)) {
      return null;
    }
    return {
      method: "get" as const,
      url: `${baseUrl}/operational-dashboard/l2`,
      params: queryParams,
    };
  }, [variant, queryParams]);

  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
  } = useFetch<OperationalDashboardResponse>(overviewRequest);

  const {
    data: l2Response,
    loading: l2Loading,
    error: l2Error,
  } = useFetch<OperationalDashboardL2Response>(l2Request);

  const l2ChartPending = useMemo(
    () =>
      isOperationsL2Variant(variant) &&
      (l2Loading || (l2Response === null && l2Error === null)),
    [variant, l2Loading, l2Response, l2Error],
  );

  const pipelineByOpportunityTypeSeries =
    useMemo((): PipelineByOpportunityTypeItem[] => {
      if (!isSalesPipelineHealthVariant(variant)) return [];
      const rows = l2Response?.data?.business?.pipeline_by_opportunity ?? [];
      if (rows.length === 0) return [];
      return rows.map((item) => ({
        type: item.type,
        value: item.count,
        percentage: item.percentage,
        color: pipelineOpportunityTypeColors[item.type],
      }));
    }, [variant, l2Response]);

  const pipelineByProjectTypeSeries =
    useMemo((): PipelineByProjectTypeItem[] => {
      if (!isSalesPipelineHealthVariant(variant)) return [];
      const rows = l2Response?.data?.business?.pipeline_by_project_type ?? [];
      if (rows.length === 0) return [];
      return rows.map((item) => ({
        type: item.type,
        value: item.count,
        percentage: item.percentage,
      }));
    }, [variant, l2Response]);

  const pipelineByLeadSourcesSeries = useMemo((): DoubleDonutCategoryItem[] => {
    if (!isSalesPipelineHealthVariant(variant)) return [];
    const rows = l2Response?.data?.business?.pipeline_by_lead_source ?? [];
    if (rows.length === 0) return [];
    return rows.map((item) => ({
      label: item.category,
      value: item.count,
      percentage: item.percentage,
      sources: item.sources.map((source) => ({
        label: source.source,
        value: source.count,
        percentage: source.percentage,
      })),
    }));
  }, [variant, l2Response]);

  const winRatioSeries = useMemo((): WinRatioTrendItem[] => {
    if (!isWinLossAnalysisVariant(variant)) return [];
    const rows = l2Response?.data?.business?.winratio_trend ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [variant, l2Response]);
  
  const pipelineTimeSeries = useMemo((): PipelineTimeSeriesItem[] => {
    if (!isSalesPipelineHealthVariant(variant)) return [];
    const months =
      l2Response?.data?.business?.weighted_pipeline_l2?.months ?? [];
    if (months.length === 0) return [];
    return [...months]
      .sort((a, b) => (a.year === b.year ? a.month - b.month : a.year - b.year))
      .map((item) => ({
        year: item.year,
        month: item.month,
        weighted: Number(item.weighted),
        new_pipeline_added: Number(item.new_pipeline_added),
      }));
  }, [variant, l2Response]);

  const getL2Rows = (data: OpsL2Data | undefined): RevenueTrendItem[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.business?.revenue?.monthly_revenue ?? [];
  };

  const revenueTrendSeries = useMemo((): RevenueTrendItem[] => {
    const rows = getL2Rows(l2Response?.data);
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const projectMarginSeries = useMemo((): ProjectMarginTrendItem[] => {
    const rows =
      l2Response?.data?.business?.project_margin?.monthly_project_margin ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const directGrossMarginSeries = useMemo((): DirectGrossMarginTrendItem[] => {
    const rows =
      l2Response?.data?.business?.direct_gross_margin
        ?.monthly_direct_gross_margin ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const clientAllocatedCostSeries =
    useMemo((): ClientAllocatedCostTrendItem[] => {
      const rows =
        l2Response?.data?.business?.delivery_cost
          ?.monthly_client_allocated_cost ?? [];
      if (rows.length === 0) return [];
      return [...rows].sort((a, b) =>
        a.year === b.year ? a.month - b.month : a.year - b.year,
      );
    }, [l2Response]);

  const benchCostSeries = useMemo((): BenchCostTrendItem[] => {
    const rows =
      l2Response?.data?.business?.delivery_cost?.monthly_bench_cost ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const internalPlusInvestmentSeries =
    useMemo((): InternalPlusInvestmentTrendItem[] => {
      const rows =
        l2Response?.data?.business?.delivery_cost
          ?.monthly_internal_plus_investment ?? [];
      if (rows.length === 0) return [];
      return [...rows].sort((a, b) =>
        a.year === b.year ? a.month - b.month : a.year - b.year,
      );
    }, [l2Response]);

  const arcDeployedSeries = useMemo((): ARCDeployedTrendItem[] => {
    const rows =
      l2Response?.data?.business?.arc_deployed?.monthly_arc_deployed ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const arcNonDeployedSeries = useMemo((): ARCNonDeployedTrendItem[] => {
    const rows =
      l2Response?.data?.business?.arc_non_deployed?.monthly_arc_non_deployed ??
      [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const averageBillRateSeries = useMemo((): AverageBillRateTrendItem[] => {
    const rows =
      l2Response?.data?.business?.average_bill_rate
        ?.monthly_average_bill_rate ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const customerMarginSeries = useMemo((): CustomerMarginTrendItem[] => {
    const rows =
      l2Response?.data?.business?.customer_margin?.monthly_customer_margin ??
      [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const ebitdaMarginSeries = useMemo((): EbitdaMarginTrendItem[] => {
    const rows =
      l2Response?.data?.business?.ebitda_margin?.monthly_ebitda_margin ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const smCostSeries = useMemo((): SmCostTrendItem[] => {
    const rows =
      l2Response?.data?.business?.sm_gross_margin?.monthly_sm_gross_margin ??
      [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const isBlendedRevenue = Boolean(
    l2Response?.data?.business?.revenue?.is_blended_revenue ?? false,
  );

  const gaGrossMarginSeries = useMemo((): GaGrossMarginTrendItem[] => {
    const rows =
      l2Response?.data?.business?.ga_gross_margin?.monthly_ga_gross_margin ??
      [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [l2Response]);

  const utilizationTrends = l2Response?.data?.business?.utilization_trends;

  const offshoreUtilizationSeries = useMemo((): UtilizationTrendItem[] => {
    const rows = utilizationTrends?.offshore_trend ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [utilizationTrends]);

  const onsiteUtilizationSeries = useMemo((): UtilizationTrendItem[] => {
    const rows = utilizationTrends?.onsite_trend ?? [];
    if (rows.length === 0) return [];
    return [...rows].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year,
    );
  }, [utilizationTrends]);

  const pipelineAgingAnalysisRows = useMemo((): PipelineAgingAnalysisItem[] => {
    if (!isSalesPipelineHealthVariant(variant)) return [];
    const rows = l2Response?.data?.business?.pipeline_ageing_trend ?? [];
    if (rows.length === 0) return [];
    return rows.map((item) => ({
      bucket:
        pipelineAgingTrendBucketLabels[item.bucket] ?? item.bucket,
      weightedPipeline: item.weightedPipeline,
      distribution: item.distribution,
    }));
  }, [l2Response]);

  React.useEffect(() => {
    const err = dashboardError ?? l2Error;
    if (err) {
      setErrorMessage(
        err.message ?? "An error occurred while loading the dashboard data",
      );
      setOpenErrorSnackbar(true);
    }
  }, [dashboardError, l2Error]);

  const handleCloseErrorSnackbar = () => {
    setOpenErrorSnackbar(false);
  };

  const navigateToRevenueMarginDetail = React.useCallback(() => {
    navigate(`/dashboard/${REVENUE_MARGIN_DASHBOARD_ID}`, {
      state: {
        opsFilters: {
          ...appliedFilters,
          dateStart: appliedFilters.dateStart ? dayjs(appliedFilters.dateStart).toISOString() : null,
          dateEnd: appliedFilters.dateEnd ? dayjs(appliedFilters.dateEnd).toISOString() : null,
        },
        opsCostType: costType,
      },
    });
  }, [navigate, appliedFilters, costType]);

  const navigateToRevenueCostAnalysis = React.useCallback(() => {
    navigate(`/dashboard/${REVENUE_COST_ANALYSIS_DASHBOARD_ID}`, {
      state: {
        opsFilters: {
          ...appliedFilters,
          dateStart: appliedFilters.dateStart ? dayjs(appliedFilters.dateStart).toISOString() : null,
          dateEnd: appliedFilters.dateEnd ? dayjs(appliedFilters.dateEnd).toISOString() : null,
        },
        opsCostType: costType,
      },
    });
  }, [navigate, appliedFilters, costType]);

  const navigateToUtilizationDashboard = React.useCallback(() => {
    navigate(`/dashboard/${UTILIZATION_DASHBOARD_ID}`, {
      state: {
        opsFilters: {
          ...appliedFilters,
          dateStart: appliedFilters.dateStart ? dayjs(appliedFilters.dateStart).toISOString() : null,
          dateEnd: appliedFilters.dateEnd ? dayjs(appliedFilters.dateEnd).toISOString() : null,
        },
        opsCostType: costType,
      },
    });
  }, [navigate, appliedFilters, costType]);

  const navigateToSalesPipelineHealth = React.useCallback(() => {
    navigate(`/dashboard/${SALES_PIPELINE_HEALTH_DASHBOARD_ID}`, {
      state: {
        opsFilters: {
          ...appliedFilters,
          dateStart: appliedFilters.dateStart ? dayjs(appliedFilters.dateStart).toISOString() : null,
          dateEnd: appliedFilters.dateEnd ? dayjs(appliedFilters.dateEnd).toISOString() : null,
        },
        opsCostType: costType,
      },
    });
  }, [navigate, appliedFilters, costType]);

  const navigateToWinLossAnalysis = React.useCallback(() => {
    navigate(`/dashboard/${WIN_LOSS_ANALYSIS_DASHBOARD_ID}`, {
      state: {
        opsFilters: {
          ...appliedFilters,
          dateStart: appliedFilters.dateStart ? dayjs(appliedFilters.dateStart).toISOString() : null,
          dateEnd: appliedFilters.dateEnd ? dayjs(appliedFilters.dateEnd).toISOString() : null,
        },
        opsCostType: costType,
      },
    });
  }, [navigate, appliedFilters, costType]);

  const dashboardBuilder = useMemo(() => {
    if (isOperationsL2Variant(variant)) {
      return undefined;
    }
    return createOperationsDashboardBuilder(dashboardData ?? undefined, {
      onRevenueClick: navigateToRevenueMarginDetail,
      onProjectMarginClick: navigateToRevenueMarginDetail,
      onCustomerMarginClick: navigateToRevenueMarginDetail,
      onDgmClick: navigateToRevenueMarginDetail,
      onEbitdaClick: navigateToRevenueMarginDetail,
      onRevenueCostAnalysisClick: navigateToRevenueCostAnalysis,
      onUtilizationClick: navigateToUtilizationDashboard,
      onSalesPipelineHealthClick: navigateToSalesPipelineHealth,
      onWinRatioClick: navigateToWinLossAnalysis,
    });
  }, [
    variant,
    dashboardData,
    navigateToRevenueMarginDetail,
    navigateToRevenueCostAnalysis,
    navigateToUtilizationDashboard,
    navigateToSalesPipelineHealth,
    navigateToWinLossAnalysis,
  ]);

  const handleMultiselectAllLogic = (
    values: string[],
    previousValues: string[],
  ): string[] => {
    if (values.length <= 1) {
      return values;
    }

    const hasAll = values.includes(EMPTY_VALUE);
    const hasSpecific = values.some((val) => val !== EMPTY_VALUE);
    const previousHasAll = previousValues.includes(EMPTY_VALUE);

    if (hasAll && hasSpecific) {
      if (!previousHasAll) {
        return [EMPTY_VALUE];
      } else {
        return values.filter((val) => val !== EMPTY_VALUE);
      }
    }

    return values;
  };

  const getPrefillValue = (
    prefillValue: string | undefined,
    hasManualChange: boolean,
    optionsLength: number,
  ): string[] => {
    if (prefillValue === undefined) {
      return [];
    }

    if (hasManualChange && optionsLength > 0) {
      return [prefillValue];
    }

    if (!hasManualChange) {
      return [prefillValue];
    }

    return [];
  };

  const handleAccountsChange = (values: string[]) => {
    const filteredValues = handleMultiselectAllLogic(
      values,
      currentFilters.accounts,
    );
    userManualChanges.current.add("accounts");

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => {
      return { ...prev, accounts: filteredValues, customers: [] };
    });
    setShouldApplyfilters(true);
  };

  React.useEffect(() => {
    if (incomingFilters !== null && !primaryOptionsLoaded.current) return;
    if (
      customerGroupOptions.length === 0 &&
      currentFilters.accounts.length > 0
    ) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        accounts: [],
      }));
    }
  }, [customerGroupOptions.length, currentFilters.accounts.length]);

  React.useEffect(() => {
    if (incomingFilters !== null && !primaryOptionsLoaded.current) return;
    if (customerOptions.length === 0 && currentFilters.customers.length > 0) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        customers: [],
      }));
    }
  }, [customerOptions.length, currentFilters.customers.length]);

  const handleCustomersChange = (values: string[]) => {
    const filteredValues = handleMultiselectAllLogic(
      values,
      currentFilters.customers,
    );

    userManualChanges.current.add("customers");

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      customers: filteredValues,
    }));
    setShouldApplyfilters(true);
  };

  const handleVerticalsChange = (value: string) => {
    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      verticals: value,
    }));
    userManualChanges.current.add("verticals");
    setShouldApplyfilters(true);
  };

  const appliedFilterLabelsCache = React.useRef<{
    customers: string[];
    accounts: string[];
    businessUnit: string;
    serviceLine: string;
    vertical: string;
  }>({
    customers: [],
    accounts: [],
    businessUnit: "",
    serviceLine: "",
    vertical: "",
  });

  const handleBusinessUnitChange = (value: string) => {
    if (value !== prefillValues.businessUnit?.value) {
      userManualChanges.current.add("businessUnit");
    }

    const hasBusinessUnitManualChange =
      userManualChanges.current.has("businessUnit");

    const accountsValue = getPrefillValue(
      prefillValues.customerGroup?.value,
      hasBusinessUnitManualChange,
      customerGroupOptions.length,
    );

    const customersValue = getPrefillValue(
      prefillValues.customer?.value,
      hasBusinessUnitManualChange,
      customerOptions.length,
    );

    const verticalsValue = hasBusinessUnitManualChange
      ? ""
      : (prefillValues.vertical?.value ?? "");

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      businessUnit: value,
      accounts: accountsValue,
      customers: customersValue,
      verticals: verticalsValue,
    }));
    setShouldApplyfilters(true);

    clearVerticalOptions();
  };

  const handleServiceLineChange = (value: string) => {
    if (value !== prefillValues.serviceLine?.value) {
      userManualChanges.current.add("serviceLine");
    }

    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      serviceLine: value,
    }));
    setShouldApplyfilters(true);
  };

  const handleDateRangeChange = (start: any, end: any, label?: string) => {
    setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
      ...prev,
      dateStart: start,
      dateEnd: end,
      dateLabel: label,
    }));
    setShouldApplyfilters(true);
  };

  const handleCostTypeChange = (newCostType: string) => {
    setCostType(newCostType);
  };

  const getDateRangeYears = () => {
    const startDate = appliedFilters.dateStart
      ? dayjs(appliedFilters.dateStart)
      : dayjs().startOf("year");
    const endDate = appliedFilters.dateEnd
      ? dayjs(appliedFilters.dateEnd)
      : dayjs().endOf("year");
    return {
      start_year: startDate.year(),
      start_month: startDate.month() + 1,
      end_year: endDate.year(),
      end_month: endDate.month() + 1,
    };
  };

  const { start_year, start_month, end_year, end_month } = getDateRangeYears();

  const reFetchCustomerOptions = () => {
    fetchCustomerOptions({
      business_unit_id: prefillValues?.businessUnit?.value ?? "",
      service_line_id: prefillValues?.serviceLine?.value ?? "",
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      start_year,
      start_month,
      end_year,
      end_month,
    });
    fetchCustomerGroupOptions({
      business_unit_id: prefillValues?.businessUnit?.value ?? "",
      service_line_id: prefillValues?.serviceLine?.value ?? "",
      start_year,
      start_month,
      end_year,
      end_month,
    });
  };

  const populateLabelCache = (filters: SimpleFilterState) => {
    const getMultiSelectLabels = (
      options: { label: string; value: string }[],
      values: string[],
    ): string[] => {
      if (!values || values.length === 0) return [];

      if (values.length === 1 && values[0] === EMPTY_VALUE) {
        return ["All"];
      }

      if (!options || options.length === 0) return [];

      const filteredValues = values.filter((v) => v !== EMPTY_VALUE);
      return mapLabels(options, filteredValues);
    };

    const customerLabels = getMultiSelectLabels(
      customerOptions,
      filters.customers,
    );
    const accountLabels = getMultiSelectLabels(
      customerGroupOptions,
      filters.accounts,
    );

    const serviceLineLabel =
      getLabel(serviceLineOptions, filters.serviceLine) ??
      (serviceLineFilterTree?.length
        ? getDisplayLabelForSelectedUnit(
            serviceLineFilterTree,
            filters.serviceLine,
          )
        : undefined) ??
      "";

    const businessUnitLabel =
      getLabel(businessUnits, filters.businessUnit) ??
      (businessUnitFilterTree?.length
        ? getDisplayLabelForSelectedUnit(
            businessUnitFilterTree,
            filters.businessUnit,
          )
        : undefined) ??
      "";

    appliedFilterLabelsCache.current = {
      customers: customerLabels,
      accounts: accountLabels,
      businessUnit: businessUnitLabel,
      serviceLine: serviceLineLabel,
      vertical: getLabel(verticalOptions, filters.verticals) ?? "",
    };
  };

  const arrayFilters: Array<{
    key: keyof SimpleFilterState;
    prefillKey: keyof typeof prefillValues;
  }> = [
    { key: "accounts", prefillKey: "customerGroup" },
    { key: "customers", prefillKey: "customer" },
  ];

  const stringFilters: Array<{
    key: keyof SimpleFilterState;
    prefillKey: keyof typeof prefillValues;
    emptyValue?: string;
  }> = [
    { key: "verticals", prefillKey: "vertical" },
    { key: "businessUnit", prefillKey: "businessUnit" },
    { key: "serviceLine", prefillKey: "serviceLine" },
  ];

  const handleApplyFilters = () => {
    const finalFilters = { ...currentFilters };

    const bothBuSlCleared =
      !finalFilters.businessUnit && !finalFilters.serviceLine;
    const bothBuSlHavePrefill =
      prefillValues.businessUnit?.value !== undefined &&
      prefillValues.serviceLine?.value !== undefined;

    if (bothBuSlCleared && bothBuSlHavePrefill) {
      finalFilters.businessUnit = prefillValues.businessUnit?.value ?? "";
      finalFilters.serviceLine = prefillValues.serviceLine?.value ?? "";
    } else if (bothBuSlCleared) {
      if (prefillValues.businessUnit?.value !== undefined) {
        finalFilters.businessUnit = prefillValues.businessUnit.value;
      }
      if (prefillValues.serviceLine?.value !== undefined) {
        finalFilters.serviceLine = prefillValues.serviceLine.value;
      }
    }

    arrayFilters.forEach(({ key, prefillKey }) => {
      const filterValue = finalFilters[key] as string[];
      if (
        (!filterValue || filterValue.length === 0) &&
        prefillValues[prefillKey]?.value === EMPTY_VALUE &&
        !userManualChanges.current.has("businessUnit") &&
        !userManualChanges.current.has("serviceLine")
      ) {
        finalFilters[key] = [EMPTY_VALUE];
      }
    });

    stringFilters.forEach(({ key, prefillKey, emptyValue }) => {
      if (
        bothBuSlCleared &&
        bothBuSlHavePrefill &&
        (key === "businessUnit" || key === "serviceLine")
      ) {
        return;
      }

      const filterValue = finalFilters[key] as string;
      const isEmpty = emptyValue ? filterValue === emptyValue : !filterValue;
      if (isEmpty && prefillValues[prefillKey]?.value === "") {
        finalFilters[key] = "";
      }
      if (
        isEmpty &&
        prefillValues[prefillKey]?.value === EMPTY_VALUE &&
        !userManualChanges.current.has("businessUnit") &&
        !userManualChanges.current.has("serviceLine")
      ) {
        finalFilters[key] = EMPTY_VALUE;
      }
    });

    populateLabelCache(finalFilters);
    setCurrentFiltersWithDebug(finalFilters);
    setAppliedFilters(finalFilters);
    setShouldApplyfilters(false);
    setIsFilterModalOpen(false);
  };

  const handleClearAllFilters = () => {
    filterRef.current?.clearAll();
    const defaultFilters = getDefaultFilters();
    setCurrentFiltersWithDebug(defaultFilters);
    setAppliedFilters(defaultFilters);
    setShouldApplyfilters(false);
    prefillApplied.current = false;
    userManualChanges.current.clear();
    appliedFilterLabelsCache.current = {
      customers: [],
      accounts: [],
      businessUnit: "",
      serviceLine: "",
      vertical: "",
    };
    fetchBusinessUnits();
    fetchServiceLineOptions();

    if (prefillValues.businessUnit?.value) {
      fetchVerticalOptions(prefillValues.businessUnit.value);
    }
  };

  const getLabel = (opts: { label: string; value: string }[], value?: string) =>
    opts.find((o) => o.value === value)?.label;

  const mapLabels = (
    opts: { label: string; value: string }[],
    values: string[],
  ) =>
    values
      .map((v) => opts.find((o) => o.value === v)?.label)
      .filter(Boolean) as string[];

  const appliedFilter = React.useMemo((): OperationsAppliedFilter => {
    const hasBusinessUnitValue = Boolean(
      appliedFilters.businessUnit && appliedFilters.businessUnit !== "",
    );

    const result = {
      Customer:
        hasBusinessUnitValue &&
        appliedFilterLabelsCache.current.customers.length > 0
          ? appliedFilterLabelsCache.current.customers
          : undefined,
      Account:
        hasBusinessUnitValue &&
        appliedFilterLabelsCache.current.accounts.length > 0
          ? appliedFilterLabelsCache.current.accounts
          : undefined,
      Business_Unit: appliedFilterLabelsCache.current.businessUnit ?? undefined,
      Service_Line: appliedFilterLabelsCache.current.serviceLine ?? undefined,
      Vertical:
        hasBusinessUnitValue && appliedFilterLabelsCache.current.vertical
          ? appliedFilterLabelsCache.current.vertical
          : undefined,
      Date_Range: appliedFilters.dateLabel,
    };
    return result;
  }, [appliedFilters]);

  const hasActiveFilters = React.useMemo(() => {
    return Object.values(appliedFilter).some(
      (value) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0),
    );
  }, [appliedFilter]);

  const getPrefillCustomerValues = () => ({
    customers:
      prefillValues.customer?.value !== undefined
        ? [prefillValues.customer.value]
        : [],
    accounts:
      prefillValues.customerGroup?.value !== undefined
        ? [prefillValues.customerGroup.value]
        : [],
  });

  const clearCustomerFilter = (): Partial<SimpleFilterState> => {
    const result = {
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
    };
    return result;
  };

  const clearAccountFilter = (): Partial<SimpleFilterState> => {
    const result = {
      accounts:
        prefillValues.customerGroup?.value !== undefined &&
        customerGroupOptions.length > 0
          ? [prefillValues.customerGroup.value]
          : [],
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
    };
    return result;
  };

  const clearBusinessUnitFilter = (): Partial<SimpleFilterState> => {
    userManualChanges.current.add("businessUnit");
    clearCustomerOptions();
    clearVerticalOptions();
    clearCustomerGroupOptions();

    const clearedFilters = {
      businessUnit: "",
      verticals: "",
      customers:
        prefillValues.customer?.value !== undefined &&
        customerOptions.length > 0
          ? [prefillValues.customer.value]
          : [],
      accounts:
        prefillValues.customerGroup?.value !== undefined &&
        customerGroupOptions.length > 0
          ? [prefillValues.customerGroup.value]
          : [],
    };
    return clearedFilters;
  };

  const clearServiceLineFilter = (): Partial<SimpleFilterState> => {
    const prefillValues_ = getPrefillCustomerValues();
    userManualChanges.current.add("serviceLine");
    if (currentFilters.businessUnit) {
      fetchCustomerGroupOptions({
        business_unit_id: appliedFilters.businessUnit ?? "",
        service_line_id: "",
        start_year,
        start_month,
        end_year,
        end_month,
      });
      fetchCustomerOptions({
        business_unit_id: appliedFilters.businessUnit ?? "",
        customer_group: appliedFilters.accounts
          .filter((val) => val !== EMPTY_VALUE)
          .join(","),
        start_year,
        start_month,
        end_year,
        end_month,
      });
    }

    return {
      serviceLine: "",
      ...prefillValues_,
    };
  };

  const clearVerticalFilter = (): Partial<SimpleFilterState> => {
    const prefillValues_ = getPrefillCustomerValues();
    reFetchCustomerOptions();

    return {
      verticals: "",
      ...prefillValues_,
    };
  };

  const clearDateRangeFilter = (): Partial<SimpleFilterState> => {
    const { startDate, endDate, label } = getVariantDefaultDateRange();
    return {
      dateStart: startDate,
      dateEnd: endDate,
      dateLabel: label,
    };
  };

  const applyBuSlPrefillLogic = (filters: SimpleFilterState) => {
    const bothBuSlWouldBeEmpty = !filters.businessUnit && !filters.serviceLine;
    const bothBuSlHavePrefill =
      prefillValues.businessUnit?.value !== undefined &&
      prefillValues.serviceLine?.value !== undefined;

    if ((bothBuSlWouldBeEmpty && bothBuSlHavePrefill) || bothBuSlWouldBeEmpty) {
      if (
        prefillValues.businessUnit?.value !== undefined &&
        prefillValues.businessUnit.value !== ""
      ) {
        filters.businessUnit = prefillValues.businessUnit.value;
      }
      if (
        prefillValues.serviceLine?.value !== undefined &&
        prefillValues.serviceLine.value !== ""
      ) {
        filters.serviceLine = prefillValues.serviceLine.value;
      }
    }
    return filters;
  };

  const handleClearFilter = (key: string) => {
    const filterClearActions: Record<string, () => Partial<SimpleFilterState>> =
      {
        Customer: clearCustomerFilter,
        Account: clearAccountFilter,
        Business_Unit: clearBusinessUnitFilter,
        Service_Line: clearServiceLineFilter,
        Vertical: clearVerticalFilter,
        Date_Range: clearDateRangeFilter,
      };

    const clearAction = filterClearActions[key];
    if (!clearAction) return;

    const updates = clearAction();
    const newAppliedFilters = applyBuSlPrefillLogic({
      ...appliedFilters,
      ...updates,
    });

    setCurrentFiltersWithDebug(newAppliedFilters);
    setAppliedFilters(newAppliedFilters);
    populateLabelCache(newAppliedFilters);
    setShouldApplyfilters(false);
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setCurrentFiltersWithDebug({ ...appliedFilters });
    setIsFilterModalOpen(false);
  };

  const toggleFilterExpanded = () => {
    setIsFilterExpanded(!isFilterExpanded);
  };

  React.useEffect(() => {
    if (!isMobile && isFilterModalOpen) {
      setIsFilterModalOpen(false);
    }
  }, [isMobile, isFilterModalOpen]);

  React.useEffect(() => {
    fetchBusinessUnits();
    fetchServiceLineOptions();
  }, []);

  const buWasLoading = React.useRef(false);
  const slWasLoading = React.useRef(false);
  React.useEffect(() => {
    if (!incomingFilters) return;
    if (loading.businessUnits) { buWasLoading.current = true; }
    if (loading.serviceLineOptions) { slWasLoading.current = true; }
    if (
      buWasLoading.current && !loading.businessUnits &&
      slWasLoading.current && !loading.serviceLineOptions
    ) {
      primaryOptionsLoaded.current = true;
    }
  }, [loading.businessUnits, loading.serviceLineOptions]);

  React.useEffect(() => {
    if (!incomingFilters) return;
    if (!primaryOptionsLoaded.current) return;

    if (incomingFilters.businessUnit) {
      fetchVerticalOptions(incomingFilters.businessUnit);
      fetchCustomerGroupOptions({
        business_unit_id: incomingFilters.businessUnit,
        service_line_id: incomingFilters.serviceLine ?? "",
        vertical: incomingFilters.verticals ?? "",
      });
      fetchCustomerOptions({
        business_unit_id: incomingFilters.businessUnit,
        service_line_id: incomingFilters.serviceLine ?? "",
        vertical: incomingFilters.verticals ?? "",
        customer_group: incomingFilters.accounts
          .filter((v) => v !== EMPTY_VALUE)
          .join(","),
      });
    } else {
      fetchCustomerGroupOptions({
        service_line_id: incomingFilters.serviceLine ?? "",
        vertical: incomingFilters.verticals ?? "",
      });
      fetchCustomerOptions({
        service_line_id: incomingFilters.serviceLine ?? "",
        vertical: incomingFilters.verticals ?? "",
        customer_group: incomingFilters.accounts
          .filter((v) => v !== EMPTY_VALUE)
          .join(","),
      });
    }
  }, [primaryOptionsLoaded.current]);

  React.useEffect(() => {
    if (!incomingFilters) return;
    if (!primaryOptionsLoaded.current) return;
    if (loading.customerGroupOptions || loading.customerOptions) return;
    const dependentOptionsReady =
      !incomingFilters.businessUnit ||
      customerGroupOptions.length > 0 ||
      customerOptions.length > 0;
    if (!dependentOptionsReady) return;

    setAppliedFilters((prev) => {
      populateLabelCache(prev);
      return { ...prev };
    });
  }, [
    primaryOptionsLoaded.current,
    loading.customerGroupOptions,
    loading.customerOptions,
    customerGroupOptions.length,
    customerOptions.length,
  ]);

  React.useEffect(() => {
    if (
      isPrimaryLoadingComplete() &&
      hasPrefillValues() &&
      !prefillApplied.current
    ) {
      const defaultFilters = getDefaultFilters();

      setCurrentFiltersWithDebug(defaultFilters);
      setAppliedFilters(defaultFilters);
      populateLabelCache(defaultFilters);
      setShouldApplyfilters(false);
      prefillApplied.current = true;
    }
  }, [
    loading.businessUnits,
    loading.serviceLineOptions,
    loading.verticals,
    prefillValues,
  ]);

  React.useEffect(() => {
    if (prefillApplied.current && hasPrefillValues()) {
      if (
        prefillValues.businessUnit?.value &&
        !userManualChanges.current.has("businessUnit")
      ) {
        fetchCustomerGroupOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          start_year,
          start_month,
          end_year,
          end_month,
        });
        fetchCustomerOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          customer_group: currentFilters.accounts
            .filter((val) => val !== EMPTY_VALUE)
            .join(","),
          start_year,
          start_month,
          end_year,
          end_month,
        });
        fetchVerticalOptions(prefillValues.businessUnit.value);
      }

      if (prefillValues.vertical?.value && prefillValues.businessUnit?.value) {
        fetchCustomerGroupOptions({
          business_unit_id: prefillValues.businessUnit.value,
          vertical: prefillValues.vertical.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          start_year,
          start_month,
          end_year,
          end_month,
        });
        fetchCustomerOptions({
          business_unit_id: prefillValues.businessUnit.value,
          vertical: prefillValues.vertical.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          customer_group: currentFilters.accounts
            .filter((val) => val !== EMPTY_VALUE)
            .join(","),
          start_year,
          start_month,
          end_year,
          end_month,
        });
      }
      if (prefillValues.businessUnit?.value) {
        fetchCustomerGroupOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          start_year,
          start_month,
          end_year,
          end_month,
        });
        fetchCustomerOptions({
          business_unit_id: prefillValues.businessUnit.value,
          service_line_id: prefillValues.serviceLine?.value ?? "",
          customer_group: currentFilters.accounts
            .filter((val) => val !== EMPTY_VALUE)
            .join(","),
          start_year,
          start_month,
          end_year,
          end_month,
        });
      }
    }
  }, [
    prefillValues.businessUnit?.value,
    prefillValues.serviceLine?.value,
    prefillValues.vertical?.value,
  ]);

  React.useEffect(() => {
    if (incomingFilters !== null) return;

    const shouldApplyPrefill =
      prefillApplied.current &&
      !userManualChanges.current.has("businessUnit") &&
      !userManualChanges.current.has("serviceLine") &&
      !userManualChanges.current.has("accounts") &&
      !userManualChanges.current.has("verticals") &&
      (prefillValues.customerGroup?.value !== undefined ||
        prefillValues.customer?.value !== undefined);
    if (shouldApplyPrefill) {
      const updatedFilters = getDefaultFilters();
      setCurrentFiltersWithDebug(updatedFilters);
      setAppliedFilters(updatedFilters);
      populateLabelCache(updatedFilters);
      setShouldApplyfilters(false);
    }
  }, [
    prefillValues.customerGroup?.value,
    prefillValues.customer?.value,
    currentFilters.businessUnit,
  ]);

  React.useEffect(() => {
    fetchCustomerGroupOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
      start_year,
      start_month,
      end_year,
      end_month,
    });
    fetchCustomerOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      start_year,
      start_month,
      end_year,
      end_month,
    });
    fetchVerticalOptions(currentFilters.businessUnit);
  }, [currentFilters.businessUnit]);

  React.useEffect(() => {
    if (
      currentFilters.businessUnit &&
      customerGroupOptions.length > 0 &&
      prefillValues.customerGroup?.value !== undefined &&
      currentFilters.accounts.length === 0 &&
      userManualChanges.current.has("businessUnit")
    ) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        accounts: [prefillValues.customerGroup?.value ?? ""],
      }));
    }
  }, [
    customerGroupOptions.length,
    currentFilters.businessUnit,
    currentFilters.accounts.length,
  ]);

  React.useEffect(() => {
    if (
      currentFilters.businessUnit &&
      customerOptions.length > 0 &&
      prefillValues.customer?.value !== undefined &&
      currentFilters.customers.length === 0 &&
      userManualChanges.current.has("businessUnit")
    ) {
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => ({
        ...prev,
        customers: [prefillValues.customer?.value ?? ""],
      }));
    }
  }, [
    customerOptions.length,
    currentFilters.businessUnit,
    currentFilters.customers.length,
  ]);

  React.useEffect(() => {
    if (
      !currentFilters.businessUnit &&
      customerGroupOptions.length > 0 &&
      prefillValues.customerGroup?.value !== undefined &&
      currentFilters.accounts.length === 0 &&
      userManualChanges.current.has("businessUnit") &&
      !userManualChanges.current.has("accounts")
    ) {
      const shouldApply = !appliedFilters.businessUnit;
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => {
        const newFilters = {
          ...prev,
          accounts: [prefillValues.customerGroup?.value ?? ""],
        };
        populateLabelCache(newFilters);
        if (shouldApply) {
          setAppliedFilters(newFilters);
          setShouldApplyfilters(false);
        }
        return newFilters;
      });
    }
  }, [
    customerGroupOptions.length,
    currentFilters.businessUnit,
    currentFilters.accounts.length,
    appliedFilters.businessUnit,
  ]);

  React.useEffect(() => {
    if (
      !currentFilters.businessUnit &&
      customerOptions.length > 0 &&
      prefillValues.customer?.value !== undefined &&
      currentFilters.customers.length === 0 &&
      userManualChanges.current.has("businessUnit") &&
      !userManualChanges.current.has("customers")
    ) {
      const shouldApply = !appliedFilters.businessUnit;
      setCurrentFiltersWithDebug((prev: SimpleFilterState) => {
        const newFilters = {
          ...prev,
          customers: [prefillValues.customer?.value ?? ""],
        };
        populateLabelCache(newFilters);
        if (shouldApply) {
          setAppliedFilters(newFilters);
          setShouldApplyfilters(false);
        }
        return newFilters;
      });
    }
  }, [
    customerOptions.length,
    currentFilters.businessUnit,
    currentFilters.customers.length,
    appliedFilters.businessUnit,
  ]);

  React.useEffect(() => {
    if (!currentFilters.businessUnit) {
      return;
    }
    fetchCustomerGroupOptions({
      business_unit_id: currentFilters.businessUnit,
      vertical: currentFilters.verticals,
      service_line_id: currentFilters.serviceLine,
      start_year,
      start_month,
      end_year,
      end_month,
    });
    fetchCustomerOptions({
      business_unit_id: currentFilters.businessUnit,
      vertical: currentFilters.verticals,
      service_line_id: currentFilters.serviceLine,
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      start_year,
      start_month,
      end_year,
      end_month,
    });
  }, [currentFilters.verticals]);

  React.useEffect(() => {
    if (!currentFilters.businessUnit) {
      return;
    }
    fetchCustomerOptions({
      business_unit_id: currentFilters.businessUnit,
      service_line_id: currentFilters.serviceLine,
      vertical: currentFilters.verticals,
      customer_group: currentFilters.accounts
        .filter((val) => val !== EMPTY_VALUE)
        .join(","),
      start_year,
      start_month,
      end_year,
      end_month,
    });
  }, [currentFilters.accounts]);

  const getFilterSectionStyles = () => ({
    ...operationsDashboardStyles.filterSection,
    width: isFilterExpanded
      ? operationsDashboardStyles.filterSection.width
      : {
          xs: "auto",
          sm: "auto",
          md: "2.5rem",
          lg: "2.5rem",
          xl: "2.5rem",
        },
    minWidth: isFilterExpanded
      ? operationsDashboardStyles.filterSection.minWidth
      : {
          xs: "auto",
          sm: "auto",
          md: "2.5rem",
        },
    maxWidth: isFilterExpanded
      ? operationsDashboardStyles.filterSection.maxWidth
      : {
          xs: "100%",
          sm: "100%",
          md: "2.5rem",
        },
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease-in-out",
    padding: isFilterExpanded ? "0.75rem" : "0.75rem 0.25rem",
  });

  const keyboardTabIcon = isFilterExpanded ? (
    <KeyboardTab sx={{ transform: "rotate(180deg)" }} />
  ) : (
    <KeyboardTab color="primary" />
  );

  const isLoading = Boolean(
    (variant === "overview" && dashboardLoading) ||
    (isOperationsL2Variant(variant) && l2Loading) ||
    loading.businessUnits ||
    loading.serviceLineOptions,
  );

  return (
    <Box sx={operationsDashboardStyles.flex1}>
      <Loading open={isLoading} />
      {!isMobile && (
        <Box sx={operationsDashboardStyles.filterContainer}>
          <ActiveFilterSection
            appliedFilter={appliedFilter as any}
            clearAllFilters={handleClearAllFilters}
            clearFilter={handleClearFilter}
            hideDateRange={true}
          />
        </Box>
      )}
      <Box sx={operationsDashboardStyles.container}>
        {!isMobile && (
          <Box sx={getFilterSectionStyles()}>
            <Box
              onClick={toggleFilterExpanded}
              sx={operationsDashboardStyles.filterSectionCollapse}
            >
              {keyboardTabIcon}
            </Box>

            {isFilterExpanded && (
              <FilterComponent
                ref={filterRef}
                values={currentFilters}
                appliedValues={appliedFilters}
                userHasChanged={shouldApplyfilters}
                businessUnits={businessUnits}
                businessUnitsLoading={loading.businessUnits}
                businessUnitFilterTree={businessUnitFilterTree}
                serviceLineOptions={serviceLineOptions}
                serviceLineOptionsLoading={loading.serviceLineOptions}
                serviceLineFilterTree={serviceLineFilterTree}
                verticalOptions={verticalOptions}
                verticalOptionsLoading={loading.verticals}
                customerGroupOptions={customerGroupOptions}
                customerGroupOptionsLoading={loading.customerGroupOptions}
                customerOptions={customerOptions}
                customerOptionsLoading={loading.customerOptions}
                onAccountsChange={handleAccountsChange}
                onCustomersChange={handleCustomersChange}
                onVerticalsChange={handleVerticalsChange}
                onBusinessUnitChange={handleBusinessUnitChange}
                onServiceLineChange={handleServiceLineChange}
                onDateRangeChange={handleDateRangeChange}
                onApply={handleApplyFilters}
                onClearAll={handleClearAllFilters}
              />
            )}
          </Box>
        )}
        <Box sx={operationsDashboardStyles.contentSection}>
          <Content
            hasActiveFilters={hasActiveFilters}
            onFilterClick={handleFilterModalOpen}
            dashboardBuilder={dashboardBuilder}
            costType={costType}
            onCostTypeChange={handleCostTypeChange}
            variant={variant}
            trendChartLoading={l2ChartPending}
            revenueTrendSeries={revenueTrendSeries}
            projectMarginSeries={projectMarginSeries}
            directGrossMarginSeries={directGrossMarginSeries}
            clientAllocatedCostSeries={clientAllocatedCostSeries}
            benchCostSeries={benchCostSeries}
            internalPlusInvestmentSeries={internalPlusInvestmentSeries}
            customerMarginSeries={customerMarginSeries}
            ebitdaMarginSeries={ebitdaMarginSeries}
            arcDeployedSeries={arcDeployedSeries}
            arcNonDeployedSeries={arcNonDeployedSeries}
            averageBillRateSeries={averageBillRateSeries}
            smCostSeries={smCostSeries}
            gaGrossMarginSeries={gaGrossMarginSeries}
            offshoreUtilizationSeries={offshoreUtilizationSeries}
            onsiteUtilizationSeries={onsiteUtilizationSeries}
            isBlendedRevenue={isBlendedRevenue}
            pipelineByOpportunityTypeSeries={pipelineByOpportunityTypeSeries}
            pipelineByLeadSourcesSeries={pipelineByLeadSourcesSeries}
            pipelineByProjectTypeSeries={pipelineByProjectTypeSeries}
            pipelineAgingAnalysisRows={pipelineAgingAnalysisRows}
            winRatioSeries={winRatioSeries}
            pipelineTimeSeries={pipelineTimeSeries}
          />
        </Box>
      </Box>

      <FilterModal
        open={isFilterModalOpen}
        onClose={handleFilterModalClose}
        values={currentFilters}
        appliedValues={appliedFilters}
        userHasChanged={shouldApplyfilters}
        businessUnits={businessUnits}
        businessUnitsLoading={loading.businessUnits}
        businessUnitFilterTree={businessUnitFilterTree}
        serviceLineOptions={serviceLineOptions}
        serviceLineOptionsLoading={loading.serviceLineOptions}
        serviceLineFilterTree={serviceLineFilterTree}
        verticalOptions={verticalOptions}
        verticalOptionsLoading={loading.verticals}
        customerGroupOptions={customerGroupOptions}
        customerGroupOptionsLoading={loading.customerGroupOptions}
        customerOptions={customerOptions}
        customerOptionsLoading={loading.customerOptions}
        onAccountsChange={handleAccountsChange}
        onCustomersChange={handleCustomersChange}
        onVerticalsChange={handleVerticalsChange}
        onBusinessUnitChange={handleBusinessUnitChange}
        onServiceLineChange={handleServiceLineChange}
        onDateRangeChange={handleDateRangeChange}
        onApply={handleApplyFilters}
        onClearAll={handleClearAllFilters}
      />

      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseErrorSnackbar}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
